import { createJSONStorage } from 'zustand/middleware';
import { createShallowStore, createStoreWithPersistAndSubscribe } from '@frontend/store';

enum ItemState {
  REGISTERED = 'REGISTERED',
  DEPRIORITIZATION_PENDING = 'DEPRIORITIZATION_PENDING',
}

type PriorityRendererItemData = {
  name: string;
  state: ItemState;
};

interface PriorityRendererGroup {
  name: string;
  priorityMap: Record<string, number>;
  items: PriorityRendererItemData[];
  isStabilized: boolean;
}

// Default stabilization timeout in milliseconds
const DEFAULT_STABILIZATION_TIMEOUT = 150;

// Store stabilization timer IDs outside of state (not persisted)
const stabilizationTimers: Record<string, ReturnType<typeof setTimeout>> = {};

export interface PriorityRendererStore {
  groupsInitializedMap?: Record<string, boolean>;
  groups: Record<string, PriorityRendererGroup>;
  initializeGroup?: (data: {
    groupName: string;
    priorityList: string[];
    resetPriorityMap?: boolean;
    stabilizationTimeout?: number;
  }) => void;
  reInitializeGroup?: (data: { groupName: string; priorityList: string[] }) => void;
  getGroup?: (groupName: string) => PriorityRendererGroup | undefined;
  registerItem?: (data: { groupName: string; itemName: string }) => void;
  markForDeprioritization?: (data: { groupName: string; itemName: string }) => void;
  unregisterItem?: (data: { groupName: string; itemName: string }) => void;
  isTopPriorityItem?: (data: { groupName: string; itemName: string }) => boolean;
  isGroupInitialized?: (groupName: string) => boolean;
  isGroupStabilized?: (groupName: string) => boolean;
  stabilizeGroup?: (groupName: string) => void;
}

export const usePriorityRendererStore = createStoreWithPersistAndSubscribe<PriorityRendererStore>(
  (set, get) => ({
    groupsInitializedMap: {},
    groups: {},

    reInitializeGroup: ({ groupName, priorityList }) => {
      // Clear any existing stabilization timer
      if (stabilizationTimers[groupName]) {
        clearTimeout(stabilizationTimers[groupName]);
        delete stabilizationTimers[groupName];
      }

      const configPriorityMap = priorityList.reduce((acc, key, index) => {
        acc[key] = index;
        return acc;
      }, {} as Record<string, number>);

      set((state) => ({
        groupsInitializedMap: {
          ...state.groupsInitializedMap,
          [groupName]: true,
        },
        groups: {
          ...state.groups,
          [groupName]: {
            name: groupName,
            priorityMap: configPriorityMap,
            items: [],
            isStabilized: false,
          },
        },
      }));
    },

    initializeGroup: ({
      groupName,
      priorityList,
      resetPriorityMap = false,
      stabilizationTimeout = DEFAULT_STABILIZATION_TIMEOUT,
    }) => {
      if (get().groupsInitializedMap?.[groupName]) {
        return;
      }

      // Clear any existing stabilization timer
      if (stabilizationTimers[groupName]) {
        clearTimeout(stabilizationTimers[groupName]);
        delete stabilizationTimers[groupName];
      }

      const group = get().groups[groupName];
      let priorityMap: Record<string, number> = {};

      const configPriorityMap = priorityList.reduce((acc, key, index) => {
        acc[key] = index;
        return acc;
      }, {} as Record<string, number>);

      if (group && Object.keys(group.priorityMap).length) {
        const deprioritizedItems = (group?.items || [])
          .filter((item) => item.state === ItemState.DEPRIORITIZATION_PENDING)
          .map((item) => item.name);

        // Cleaning up deprioritized items from priorityMap
        priorityMap = Object.fromEntries(
          Object.entries(group.priorityMap).filter(([key]) => !deprioritizedItems.includes(key))
        );
      }

      // If resetPriorityMap is true, we reset the priorityMap to the configPriorityMap
      if (resetPriorityMap) {
        priorityMap = configPriorityMap;
      }
      // If priority list is empty or all items are deprioritized then reset the priorityMap
      if (!Object.keys(priorityMap).length) {
        priorityMap = configPriorityMap;
      }

      set((state) => ({
        groupsInitializedMap: {
          ...state.groupsInitializedMap,
          [groupName]: true,
        },
        groups: {
          ...state.groups,
          [groupName]: {
            name: groupName,
            priorityMap,
            items: [],
            isStabilized: false,
          },
        },
      }));

      // Start stabilization timer - group will stabilize after timeout even if not all items register
      stabilizationTimers[groupName] = setTimeout(() => {
        get().stabilizeGroup?.(groupName);
        delete stabilizationTimers[groupName];
      }, stabilizationTimeout);
    },

    getGroup: (groupName) => {
      if (!get().isGroupInitialized?.(groupName)) {
        return;
      }
      const group = get().groups[groupName];
      if (!group) {
        console.error(`Group with name ${groupName} not found`);
        return;
      }
      return group;
    },

    isGroupInitialized: (groupName) => !!get().groupsInitializedMap?.[groupName],

    isGroupStabilized: (groupName) => {
      const group = get().groups[groupName];
      return group?.isStabilized ?? false;
    },

    stabilizeGroup: (groupName) => {
      const group = get().groups[groupName];
      if (!group || group.isStabilized) {
        return;
      }

      set((state) => ({
        ...state,
        groups: {
          ...state.groups,
          [groupName]: {
            ...state.groups[groupName],
            isStabilized: true,
          },
        },
      }));
    },

    isTopPriorityItem: ({ groupName, itemName }) => {
      const group = get().getGroup?.(groupName);
      return group?.items[0]?.name === itemName;
    },

    registerItem: ({ groupName, itemName }) => {
      const group = get().getGroup?.(groupName);
      if (!group) {
        return;
      }

      const isItemRegistered = group.items.find((item) => item.name === itemName);

      if (isItemRegistered) {
        return;
      }

      const items = [...group.items];
      const itemPriority = group.priorityMap[itemName];
      const newItem: PriorityRendererItemData = {
        name: itemName,
        state: ItemState.REGISTERED,
      };

      const insertIndex = items.findIndex((item) => {
        return group.priorityMap[item.name] === undefined || group.priorityMap[item.name] > itemPriority;
      });

      items.splice(insertIndex === -1 ? items.length : insertIndex, 0, newItem);

      set((state) => ({
        ...state,
        groups: {
          ...state.groups,
          [groupName]: {
            ...state.groups[groupName],
            items,
          },
        },
      }));
    },

    markForDeprioritization: ({ groupName, itemName }) => {
      const group = get().getGroup?.(groupName);
      if (!group) {
        return;
      }

      const itemIndex = group.items.findIndex((item) => item.name === itemName);

      if (itemIndex === -1) {
        console.error(`Item with name ${itemName} not found in group ${groupName}`);
        return;
      }
      const updatedItem = { ...group.items[itemIndex], state: ItemState.DEPRIORITIZATION_PENDING };

      set((state) => ({
        ...state,
        groups: {
          ...state.groups,
          [groupName]: {
            ...state.groups[groupName],
            items: [...group.items.slice(0, itemIndex), updatedItem, ...group.items.slice(itemIndex + 1)],
          },
        },
      }));
    },

    unregisterItem: ({ groupName, itemName }) => {
      const group = get().getGroup?.(groupName);
      const groupsInitializedMap = get().groupsInitializedMap;
      if (!group || !groupsInitializedMap?.[groupName]) {
        return;
      }

      // Remove registered item but ignore deprioritization pending items
      // Deprioritization pending items will be removed on next initialization
      const groupItems = group.items.filter((item) => !(item.name === itemName && item.state === ItemState.REGISTERED));
      set((state) => ({
        ...state,
        groups: {
          ...state.groups,
          [groupName]: {
            ...state.groups[groupName],
            items: groupItems,
          },
        },
      }));
    },
  }),
  {
    name: 'priority-renderer-store',
    partialize: (state) => ({
      groups: state.groups,
    }),
    storage: createJSONStorage(() => localStorage),
  },
  { name: 'priority-renderer-store', trace: true }
);

export const usePriorityRendererShallowStore = createShallowStore<PriorityRendererStore>(usePriorityRendererStore);
